import { useContext, useEffect, useReducer } from "react";
import { sendRequest } from "@/common/http";
import { AppContextInterface, AppContext } from "@/contexts/AppContext";
import ApiError from "@/features/common/elements/ApiError";
import WidgetLoader from "@/features/common/elements/WidgetLoader";
import translate from "@/i18n/translate";

export interface RecentCourseEvaluationResult {
  courseTitle: string;
  courseId: string;
  courseCode: string;
  projectTitle: string;
  projectId: string;
  url: string;
}
interface State {
  courseEvaluationResults: RecentCourseEvaluationResult[] | null;
  courseEvaluationResultsError: boolean;
  showLoader: boolean;
}
interface Action {
  type: string;
  courseEvaluationResults: RecentCourseEvaluationResult[] | null;
}
const defaultAction: Action = {
  type: "",
  courseEvaluationResults: null
};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case "setCourseEvaluationResults":
      return {
        ...state,
        courseEvaluationResults: action.courseEvaluationResults,
        showLoader: false
      };
    case "setCourseEvaluationResultsError":
      return {
        ...state,
        courseEvaluationResultsError: true,
        showLoader: false
      };
    default:
      return state;
  }
};

const initialState: State = {
  courseEvaluationResults: null,
  courseEvaluationResultsError: false,
  showLoader: true
};

export function RecentCourseEvaluation() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { sysUser } = useContext<AppContextInterface>(AppContext);

  useEffect(() => {
    const applyResult = (result: RecentCourseEvaluationResult[]) => {
      dispatch({
        ...defaultAction,
        type: "setCourseEvaluationResults",
        courseEvaluationResults: result
      });
    };
    const onError = () => {
      dispatch({
        ...defaultAction,
        type: "setCourseEvaluationResultsError"
      });
    };
    sendRequest(
      { uri: "/ces/recent-course-evaluation", camelize: true },
      applyResult,
      onError
    );
  }, []);

  const navigateToExternalTask = (url: string) => {
    window.location.href = url;
  };

  const handleKeyPressFs = (
    event: React.KeyboardEvent<HTMLDivElement>,
    url: string
  ) => {
    if (event.key === " " || event.key === "Enter") {
      navigateToExternalTask(url);
    }
  }

  return (
    (state.courseEvaluationResults && state.courseEvaluationResults.length > 0) && (
      <div className="recent-course-evaluation-container">
        <div
          className="widget-card recent-course-evaluation-container paper-card"
        >
          <div className="card-header header">
            <h3 className="recent-course-heading title">
              {translate("app.home.recentCourseEvaluation.title")}
            </h3>
            <div className="base-font">
              {translate("app.home.recentCourseEvaluation.desc")}
            </div>
          </div>

          <div className="body">
            {state.showLoader && (
              <WidgetLoader
                loaderContainerClasses="loader"
                loaderClasses="content"
              />
            )}

            {state.courseEvaluationResultsError && (
              <div className="widget-body">
                <ApiError />
              </div>
            )}

            {!state.courseEvaluationResultsError && !state.showLoader && state.courseEvaluationResults && state.courseEvaluationResults.length > 0 && (
              <div className="widget-body">
                <div className="ai-banner">
                  <p>
                    {translate("app.home.recentCourseEvaluation.banner.text")}
                  </p>
                </div>
                {state.courseEvaluationResults.map((result, index) => (
                  <div
                    className="recent-course"
                    onClick={() => navigateToExternalTask(result.url)}
                    onKeyDown={(event) => handleKeyPressFs(event, result.url)}
                    key={`${result.courseId}-${index}`}
                  >
                    <div className="flex-row recent-course-evaluation-link">
                      <a className="recent-course-link" href={result.url}>
                        <span className="read-more">
                          {result.courseCode}&emsp;•&emsp;{result.courseTitle}
                        </span>
                      </a>
                    </div>

                    <div className="recent-course-project-title">
                      {result.projectTitle}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!state.courseEvaluationResultsError && !state.showLoader && state.courseEvaluationResults && state.courseEvaluationResults.length == 0 && (
              <div className="widget-body empty-state-course-evaluation">
                {translate("app.home.recentCourseEvaluation.emptyState.text")}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );
}